import type {
  AccountInfo,
  IPublicClientApplication,
} from '@azure/msal-browser';

import { isAuthSessionExpiredError } from '@shared/errors/auth-session-expired-error';
import { acquireAccessToken } from '@shared/helpers/acquire-access-token/acquireAccessToken';
import { extractAuthData } from '@shared/helpers/extract-auth-data/extractAuthData';
import { clearMsalStorage } from '@shared/helpers/msal-instance/msalInstance.utils';
import { handleSessionExpired } from '@shared/helpers/session-expiry/handleSessionExpired';

import type { AuthState } from './AuthProvider.types';

export const INITIALIZING_STATE: AuthState = { status: 'initializing' };
export const UNAUTHENTICATED_STATE: AuthState = { status: 'unauthenticated' };

function isAuthorityMismatch(error: unknown): boolean {
  return (
    (error as { errorCode?: string } | null)?.errorCode === 'authority_mismatch'
  );
}

/**
 * Validates the cached MSAL session (refreshing the ID token if needed) and maps the outcome
 * to an AuthState. An unrenewable session is flagged in the session-expired store.
 */
export async function resolveAuthState(
  instance: IPublicClientApplication,
  accounts: AccountInfo[]
): Promise<AuthState> {
  if (accounts.length === 0) {
    return UNAUTHENTICATED_STATE;
  }

  if (!instance.getActiveAccount()) {
    instance.setActiveAccount(accounts[0]);
  }

  let idToken: string;
  try {
    idToken = await acquireAccessToken();
  } catch (error: unknown) {
    if (isAuthSessionExpiredError(error)) {
      await handleSessionExpired();
    } else if (isAuthorityMismatch(error)) {
      clearMsalStorage();
    } else {
      return { status: 'error' };
    }
    return UNAUTHENTICATED_STATE;
  }

  try {
    return { status: 'authenticated', authData: extractAuthData(idToken) };
  } catch {
    return UNAUTHENTICATED_STATE;
  }
}
